'use client';

// react
import { useCallback, useEffect, useMemo, useState } from 'react';

export type ValidityState = Record<string, boolean>;
export type SetIsValid = (isValid: boolean) => void;

/**
 * useValidator is a hook that provides a way to validate a form.
 */
export const useValidator = (setIsValid?: SetIsValid) => {
  // state
  const [validity, setValidity] = useState<ValidityState>({});

  // memos
  const isValid = useMemo(
    () => Object.values(validity).every((v) => v),
    [validity],
  );

  // methods
  const createSetIsValid = useCallback((key: string) => {
    return (isValid: boolean) =>
      setValidity((prev) => {
        if (prev[key] === isValid) return prev;
        return { ...prev, [key]: isValid };
      });
  }, []);

  // memos
  const memoizedCreateSetIsValid = useMemo(() => {
    const cache: Record<string, SetIsValid> = {};
    return (key: string) => {
      if (!cache[key]) {
        cache[key] = createSetIsValid(key);
      }
      return cache[key];
    };
  }, [createSetIsValid]);

  // effects
  useEffect(() => {
    if (!setIsValid) return;
    const isValid = Object.values(validity).every((v) => v);
    setIsValid(isValid);
  }, [validity, setIsValid]);

  return {
    isValid,
    validity,
    createSetIsValid: memoizedCreateSetIsValid,
  };
};
